/**
 * InstallCTA — Phase 8 (M8) public surface for `/instalar`.
 *
 * Public install page. Renders one of three platform branches:
 *
 *   - iOS     → "Adicionar à Tela de Início" + App Store badge link
 *   - Android → "Instalar pelo Play Store" + Play Store badge link
 *   - Desktop → QR code targeting the install URL, so the visitor can
 *     scan with their phone and continue the flow there.
 *
 * The App Store URL is live and the Play Store URL carries the real
 * package id (see `lib/install/deepLink.ts`) — the two constants are the
 * single source every surface picks up.
 *
 * Detection happens via `getInstallPlatform()` which reads
 * `navigator.userAgent`. The component accepts a `platform` prop for
 * tests + future server-side hints.
 */
import type { CSSProperties } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  APP_STORE_URL,
  INSTALL_URL_PATH,
  PLAY_STORE_URL,
  getInstallPlatform,
  type InstallPlatform,
} from '@/lib/install/deepLink'

import { InstallQrCode } from './InstallQrCode'

interface InstallCTAProps {
  /** Override platform (tests + future SSR hints). */
  platform?: InstallPlatform
  /** Override origin used to build the QR url (tests only). */
  originForQr?: string
  /**
   * Gate mode (web-hardening at `/`): when set, this is the install GATE shown
   * to unauthed visitors before the login. The store/QR promo stays the hero;
   * a *discreet* "continuar na versão web" link at the bottom navigates here
   * (e.g. `/login`), and the back-to-landing link is suppressed (the gate IS
   * the entry — "voltar" would loop). Omitted on the standalone `/instalar`
   * page, which keeps its back-to-`/` link.
   */
  webContinueHref?: string
}

const page: CSSProperties = {
  minHeight: '100dvh',
  background:
    'linear-gradient(180deg, color-mix(in srgb, var(--color-primary, #0d9488) 8%, var(--color-bg, #ffffff)) 0%, var(--color-bg, #ffffff) 60%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 16px',
}

const card: CSSProperties = {
  width: '100%',
  maxWidth: 520,
  background: 'white',
  borderRadius: 20,
  padding: 28,
  boxShadow: '0 18px 48px rgba(15, 23, 42, 0.10)',
  border: '1px solid var(--color-border, #e2e8f0)',
  color: 'var(--color-fg, #0f172a)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  alignItems: 'center',
  textAlign: 'center',
}

const kicker: CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--color-primary, #0d9488)',
}

const title: CSSProperties = {
  fontSize: 22,
  fontWeight: 800,
  margin: 0,
}

const subtitle: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.5,
  color: 'var(--color-muted, #475569)',
  margin: 0,
}

const ctaPrimary: CSSProperties = {
  background: 'var(--color-primary, #0d9488)',
  color: 'white',
  padding: '14px 24px',
  borderRadius: 999,
  fontSize: 15,
  fontWeight: 700,
  textDecoration: 'none',
  border: 'none',
  display: 'inline-block',
}

const ctaGhost: CSSProperties = {
  background: 'transparent',
  color: 'var(--color-fg, #0f172a)',
  padding: '10px 22px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
  border: '1.5px solid var(--color-border, #e2e8f0)',
  display: 'inline-block',
}

const featuresList: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  textAlign: 'left',
  width: '100%',
  maxWidth: 360,
}

const featureItem: CSSProperties = {
  fontSize: 13,
  color: 'var(--color-fg, #0f172a)',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
}

const featureBullet: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: 999,
  background: 'var(--color-primary, #0d9488)',
  flexShrink: 0,
}

const comingSoon: CSSProperties = {
  fontSize: 12,
  color: 'var(--color-muted, #64748b)',
  marginTop: 8,
}

// Gate mode: the "continuar na versão web" escape hatch is deliberately
// DISCREET — a small muted text link, not a button — so the app download
// stays the clear primary action (owner's brief).
const webContinue: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--color-muted, #64748b)',
  fontSize: 13,
  fontWeight: 500,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  marginTop: 4,
  cursor: 'pointer',
}

export function InstallCTA({
  platform,
  originForQr,
  webContinueHref,
}: InstallCTAProps) {
  const { t } = useTranslation('common')

  const detected = useMemo<InstallPlatform>(
    () => platform ?? getInstallPlatform(),
    [platform],
  )

  const qrTarget = useMemo(() => {
    const origin =
      originForQr ??
      (typeof window !== 'undefined' ? window.location.origin : 'https://cuidari.com')
    return `${origin}${INSTALL_URL_PATH}`
  }, [originForQr])

  const platformCopyKey =
    detected === 'ios'
      ? 'install.platform.ios'
      : detected === 'android'
      ? 'install.platform.android'
      : 'install.platform.desktop'

  return (
    <main data-testid="install-cta" data-platform={detected} style={page}>
      <section style={card}>
        <span style={kicker}>{t('install.kicker')}</span>
        <h1 style={title}>{t('install.title')}</h1>
        <p style={subtitle}>{t('install.subtitle')}</p>

        <p
          data-testid="install-platform-line"
          style={{ ...subtitle, fontWeight: 600 }}
        >
          {t(platformCopyKey, { defaultValue: '' })}
        </p>

        {detected === 'ios' && (
          <a
            data-testid="install-cta-appstore"
            href={APP_STORE_URL}
            style={ctaPrimary}
            rel="noreferrer"
          >
            {t('install.cta.appStore')}
          </a>
        )}

        {detected === 'android' && (
          <a
            data-testid="install-cta-playstore"
            href={PLAY_STORE_URL}
            style={ctaPrimary}
            rel="noreferrer"
          >
            {t('install.cta.playStore')}
          </a>
        )}

        {detected === 'desktop' && (
          <>
            <InstallQrCode
              value={qrTarget}
              ariaLabel={t('install.qr.label')}
            />
            <p style={{ ...subtitle, fontSize: 12 }}>
              {t('install.qr.caption')}
            </p>
          </>
        )}

        <ul data-testid="install-features" style={featuresList}>
          <li style={featureItem}>
            <span aria-hidden style={featureBullet} />
            {t('install.features.items.remind')}
          </li>
          <li style={featureItem}>
            <span aria-hidden style={featureBullet} />
            {t('install.features.items.track')}
          </li>
          <li style={featureItem}>
            <span aria-hidden style={featureBullet} />
            {t('install.features.items.share')}
          </li>
        </ul>

        <span style={comingSoon}>{t('install.comingSoon')}</span>

        {webContinueHref ? (
          <a
            data-testid="install-continue-web"
            href={webContinueHref}
            style={webContinue}
          >
            {t('install.cta.continueWeb')}
          </a>
        ) : (
          <a data-testid="install-back" href="/" style={ctaGhost}>
            {t('install.cta.back')}
          </a>
        )}
      </section>
    </main>
  )
}
